// uiarc: switch (https://uiarc.dev/r/switch.json) — Radix yerine Base UI, CSS Modules yerine Tailwind.
// 42x24 iz, 18px başparmak. Başparmak basılıyken karşı yöne 5px uzar (tutan parmak gibi), sonra kritik sönümlü
// yayla uca iner; durum tuşla/kodla değişirse kısa bir kütle uzaması (scaleX 1→1.16→1) yapar. İz iki dolgu arasında
// solar (`control-track` ↔ `control-on`). `prefers-reduced-motion` altında hareket yok.
// `size="sm"` uiarc'ta yok: aynı oranlarla 32x20 iz, 14px başparmak. uiarc'taki `label` bu projede ayrı `Label` ile.
import { Switch as SwitchPrimitive } from '@base-ui/react/switch'
import { animate, motion, useMotionValue, useReducedMotion } from 'motion/react'
import type { Transition } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

/** Kritik sönümlü: başparmak bildirdiği durumu aşmadan uca oturur. */
const glide: Transition = { type: 'spring', visualDuration: 0.3, bounce: 0 }

const dimensions = {
  default: { thumb: 18, travel: 18, stretch: 5 },
  sm: { thumb: 14, travel: 12, stretch: 4 },
} as const

function Switch({
  className,
  size = 'default',
  checked,
  defaultChecked,
  onCheckedChange,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onKeyDown,
  onKeyUp,
  onBlur,
  ...props
}: SwitchPrimitive.Root.Props & {
  size?: 'sm' | 'default'
}) {
  const reduceMotion = useReducedMotion()
  const { thumb, travel, stretch } = dimensions[size]
  const [internal, setInternal] = useState(defaultChecked ?? false)
  const [pressed, setPressed] = useState(false)
  const on = checked ?? internal
  // Kısa kütle uzaması: başparmak kayan bir nokta değil, hareket eden bir kütle gibi okunur.
  const scaleX = useMotionValue(1)
  const shown = useRef(on)
  // Fare/Space basışı başparmağı zaten uzattı; bırakınca ikinci bir uzama eklenmesin.
  const releasedAt = useRef(-Infinity)
  useEffect(() => {
    if (shown.current === on) return
    shown.current = on
    const fromPress = performance.now() - releasedAt.current < 250
    if (reduceMotion || fromPress) return
    const controls = animate(scaleX, [1, 1.16, 1], {
      duration: 0.34,
      times: [0, 0.4, 1],
      ease: ['easeOut', 'easeInOut'],
    })
    return () => controls.stop()
  }, [on, reduceMotion, scaleX])
  const extra = pressed && !reduceMotion && !props.disabled ? stretch : 0

  const release = () => {
    if (pressed && !props.disabled) releasedAt.current = performance.now()
    setPressed(false)
  }

  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      data-size={size}
      {...props}
      checked={on}
      onCheckedChange={(next, details) => {
        if (checked === undefined) setInternal(next)
        onCheckedChange?.(next, details)
      }}
      onPointerDown={(event) => {
        onPointerDown?.(event)
        if (event.button === 0) setPressed(true)
      }}
      onPointerUp={(event) => {
        onPointerUp?.(event)
        release()
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event)
        setPressed(false)
      }}
      onPointerCancel={(event) => {
        onPointerCancel?.(event)
        setPressed(false)
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event)
        if (event.key === ' ') setPressed(true)
      }}
      onKeyUp={(event) => {
        onKeyUp?.(event)
        release()
      }}
      onBlur={(event) => {
        onBlur?.(event)
        setPressed(false)
      }}
      className={cn(
        'peer bg-control-track ease-standard rounded-pill before:bg-control-on before:ease-standard not-data-disabled:not-data-checked:hover:bg-control-track-hover relative isolate inline-flex shrink-0 cursor-pointer items-center border-0 p-[3px] transition-[background-color] duration-(--duration-fast) [-webkit-tap-highlight-color:transparent] before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:opacity-0 before:transition-opacity before:duration-(--duration-standard) before:content-[""] data-checked:before:opacity-100 data-disabled:cursor-not-allowed data-disabled:opacity-50 data-[size=default]:h-6 data-[size=default]:w-[42px] data-[size=sm]:h-5 data-[size=sm]:w-8 motion-reduce:transition-none motion-reduce:before:transition-none',
        className,
      )}
    >
      {/* Başparmak tutan parmak gibi uzar, uzak kenarı sabit kalır; sonra yayla yürür. */}
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className="bg-control-thumb data-checked:bg-control-thumb-on ease-standard rounded-pill pointer-events-none block flex-none shadow-(--control-thumb-shadow) transition-[background-color] duration-(--duration-standard) will-change-transform motion-reduce:transition-none"
        style={{ height: thumb }}
        render={
          <motion.span
            style={{ scaleX }}
            initial={false}
            animate={{ x: on ? travel - extra : 0, width: thumb + extra }}
            transition={
              reduceMotion ? { duration: 0 } : { x: glide, width: motionTokens.spring.snappy }
            }
          />
        }
      />
    </SwitchPrimitive.Root>
  )
}

export { Switch }
